import { Link } from '@tanstack/react-router';
import { ChevronLeft, Menu } from 'lucide-react';

import { Button } from '@/components/ui/button';
import type { Crumb } from '@/lib/active-screen';

import { FetchIndicator } from './FetchIndicator';
import { ThemeToggle } from './ThemeToggle';

interface TopBarProps {
  readonly title: string;
  readonly crumb: Crumb | null;
  readonly onOpenNav: () => void;
}

export function TopBar({ title, crumb, onOpenNav }: TopBarProps) {
  const back = crumb?.back;

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-border bg-background px-4 sm:px-[22px]">
      <Button
        variant="ghost"
        size="icon"
        onClick={onOpenNav}
        aria-label="Open navigation"
        className="-ml-1 shrink-0 lg:hidden"
      >
        <Menu aria-hidden="true" />
      </Button>

      {/*
        The way out of a sub-route lives here rather than on each detail
        screen: "up" is then in the same place on every screen, and a detail
        route added later inherits it from its path instead of remembering to
        draw its own.
      */}
      {back === undefined ? null : (
        <Button variant="outline" size="xs" asChild className="shrink-0 text-muted-foreground">
          {/*
            Named "Back to X" rather than "X": the sidebar already has a link
            called "Exercise library", and two links with one name is a coin
            flip for anyone navigating by name.
          */}
          <Link to={back.path} aria-label={`Back to ${back.label}`}>
            <ChevronLeft aria-hidden="true" className="size-3.5" />
            {back.label}
          </Link>
        </Button>
      )}

      <h1 className="truncate text-[15px] font-semibold tracking-tight text-foreground">{title}</h1>

      {crumb?.hint === undefined ? null : (
        <span className="hidden truncate text-[12.5px] text-muted-foreground md:inline">{crumb.hint}</span>
      )}

      <div className="ml-auto flex items-center gap-2.5">
        <FetchIndicator />
        <ThemeToggle className="shrink-0" />
      </div>
    </header>
  );
}
